import { Alert, Snackbar } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { fileQueryKeys } from "@/shared/domain/files/hooks/fileQueryKeys";

import { CancelUploadDialog } from "./CancelUploadDialog";
import { ExportJobToast } from "./ExportJobToast";
import { FileExportService } from "./FileExportService";
import { FileTransferContext } from "./FileTransferContext";
import styles from "./FileTransferProvider.module.css";
import { getUploadItems } from "./getUploadItems";
import {
  FileUploadAction,
  UploadProviderState,
  UploadFileActionTypes,
  UIUploadItem,
  UploadItem,
} from "./types";
import { UploadProgressToast } from "./UploadProgressToast";
import { uploadReducer } from "./uploadReducer";
import { useUploadCoordinator } from "./useUploadCoordinator";

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface FileTransferProviderProps {
  children: React.ReactNode;
}

export const FileTransferProvider: React.FC<FileTransferProviderProps> = ({
  children,
}) => {
  const { files } = useDomainServices();

  // Always reflect the latest FileService in the ref so that FileExportService
  // polls with the current HttpClient (which carries the resolved resourceOwnerId).
  const filesRef = React.useRef(files);
  filesRef.current = files;

  // Upload state
  const [uploadState, uploadDispatch] = React.useReducer<
    React.Reducer<UploadProviderState, FileUploadAction>
  >(uploadReducer, {
    isCancelDialogOpen: false,
    isCancelUploadInProgress: false,
    isProcessingFiles: false,
    isToastVisible: false,
    processingError: null,
    uploadItems: {},
  });

  const queryClient = useQueryClient();

  // ---------------------------
  // Export job service
  // ---------------------------

  const exportService = React.useMemo(
    () =>
      new FileExportService(
        () => filesRef.current,
        (url) => {
          const a = document.createElement("a");
          a.href = url;
          a.download = "export.zip";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        },
      ),
    [],
  );

  // Tear down all polling intervals when the provider unmounts.
  React.useEffect(() => {
    return () => exportService.destroy();
  }, [exportService]);

  const beginExportJob = React.useCallback(
    (jobId: string, label: string) => {
      exportService.startJob(jobId, label);
    },
    [exportService],
  );

  const [showDismissWarning, setShowDismissWarning] = React.useState(false);

  const handleExportToastClose = React.useCallback(() => {
    if (exportService.hasActiveJobs()) {
      setShowDismissWarning(true);
    }
    exportService.closeToast();
  }, [exportService]);

  // ---------------------------
  // Upload state helpers
  // ---------------------------

  let isUploading = false;
  Object.values(uploadState.uploadItems).forEach((item) => {
    if (!item.isUploadCompleted) {
      isUploading = true;
    }
  });

  // ---------------------------
  // Upload callbacks
  // ---------------------------

  const handleUploadItemBegin = React.useCallback((itemId: string) => {
    uploadDispatch({
      type: UploadFileActionTypes.UploadItemBegin,
      payload: { itemId },
    });
  }, []);

  const handleUploadItemBeginCancellation = React.useCallback(
    (itemId: string) => {
      uploadDispatch({
        type: UploadFileActionTypes.UploadItemBeginCancellation,
        payload: { itemId },
      });
    },
    [],
  );

  const handleUploadItemCancelled = React.useCallback((itemId: string) => {
    uploadDispatch({
      type: UploadFileActionTypes.UploadItemCancelled,
      payload: { itemId },
    });
  }, []);

  const handleUploadItemProgress = React.useCallback(
    (itemId: string, bytesUploaded: number) => {
      uploadDispatch({
        type: UploadFileActionTypes.UploadItemProgress,
        payload: { itemId, bytesUploaded },
      });
    },
    [],
  );

  const handleUploadItemComplete = React.useCallback((itemId: string) => {
    uploadDispatch({
      type: UploadFileActionTypes.UploadItemComplete,
      payload: { itemId },
    });
  }, []);

  const handleUploadItemError = React.useCallback(
    (itemId: string, error: Error) => {
      uploadDispatch({
        type: UploadFileActionTypes.UploadItemError,
        payload: { itemId, error },
      });
    },
    [],
  );

  const handleUploadItemFirstBytes = React.useCallback((itemId: string) => {
    uploadDispatch({
      type: UploadFileActionTypes.UploadItemFirstBytes,
      payload: { itemId },
    });
  }, []);

  const { cancelInProgressUploads, coordinateUploadItems } =
    useUploadCoordinator(
      handleUploadItemBegin,
      handleUploadItemBeginCancellation,
      handleUploadItemCancelled,
      handleUploadItemProgress,
      handleUploadItemComplete,
      handleUploadItemError,
      handleUploadItemFirstBytes,
    );

  const beginFileUpload = React.useCallback(
    async (
      event: React.DragEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>,
      orgId: string,
      datasetId: string,
      prefix?: string,
    ) => {
      const nativeEvent = event.nativeEvent;
      nativeEvent.preventDefault();

      uploadDispatch({ type: UploadFileActionTypes.BeginProcessing });

      let uploadItems: UploadItem[] = [];
      let uiItems: UIUploadItem[] = [];

      try {
        uploadItems = await getUploadItems(
          nativeEvent,
          orgId,
          datasetId,
          prefix,
        );

        uiItems = uploadItems.map((classItem) => {
          const item: UIUploadItem = {
            created: Date.now(),
            id: classItem.id,
            name: classItem.name,
            bytesUploaded: 0,
            totalBytes: classItem.totalBytes,
            isCancelling: false,
            isCancelled: false,
            isUploadCompleted: false,
            isUploadStarted: false,
            prefix: classItem.prefix,
            relative_path: classItem.path,
            dataset_id: classItem.datasetId,
            type: classItem.type,
          };
          return item;
        });
      } catch (reason) {
        if (reason instanceof Error) {
          uploadDispatch({
            type: UploadFileActionTypes.ProcessingError,
            payload: { error: reason },
          });
        } else {
          uploadDispatch({
            type: UploadFileActionTypes.ProcessingError,
            payload: { error: new Error("Unknown error processing files") },
          });
        }
      }

      uploadDispatch({
        type: UploadFileActionTypes.EndProcessingBeginUpload,
        payload: { uploadItems: uiItems },
      });

      await coordinateUploadItems(uploadItems);
      // The association now holds entries at the uploaded paths, which the
      // lookups behind the Overridden labels and the URDF preview report.
      await queryClient.invalidateQueries({
        queryKey: fileQueryKeys.lookups(datasetId),
      });
    },
    [coordinateUploadItems, queryClient],
  );

  const onExitUploadToastClicked = () => {
    const inProgressUploads = Object.values(uploadState.uploadItems).filter(
      (item) =>
        !item.isUploadCompleted &&
        item.isUploadStarted &&
        !item.isCancelling &&
        !item.isCancelled,
    );

    if (inProgressUploads.length > 0) {
      uploadDispatch({ type: UploadFileActionTypes.ToggleCancelDialog });
    } else {
      uploadDispatch({ type: UploadFileActionTypes.CloseToastDisplay });
    }
  };

  return (
    <FileTransferContext.Provider
      value={React.useMemo(
        () => ({ beginFileUpload, isUploading, beginExportJob }),
        [beginFileUpload, isUploading, beginExportJob],
      )}
    >
      <CancelUploadDialog
        open={uploadState.isCancelDialogOpen}
        isCancelUploadInProgress={uploadState.isCancelUploadInProgress}
        onCancelUpload={async () => {
          uploadDispatch({ type: UploadFileActionTypes.BeginCancelUpload });
          await cancelInProgressUploads();
          await queryClient.refetchQueries({
            queryKey: fileQueryKeys.directoryContents(),
          });
          uploadDispatch({ type: UploadFileActionTypes.CloseToastDisplay });
        }}
        onContinueUpload={() => {
          uploadDispatch({ type: UploadFileActionTypes.ToggleCancelDialog });
        }}
      />

      {/* Stacked toast container: upload on top, export on bottom */}
      <div className={styles.toastContainer}>
        <UploadProgressToast
          isVisible={uploadState.isToastVisible}
          isProcessing={uploadState.isProcessingFiles}
          onExitClicked={onExitUploadToastClicked}
          processingError={uploadState.processingError}
          uploadItems={Object.values(uploadState.uploadItems).sort(
            (a, b) => a.created - b.created,
          )}
        />
        <ExportJobToast
          service={exportService}
          isStackedBelow={uploadState.isToastVisible}
          onExitClicked={handleExportToastClose}
        />
      </div>

      <Snackbar
        open={showDismissWarning}
        autoHideDuration={5000}
        onClose={() => setShowDismissWarning(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="info" onClose={() => setShowDismissWarning(false)}>
          Your ZIP will download automatically when ready.
        </Alert>
      </Snackbar>

      {children}
    </FileTransferContext.Provider>
  );
};
